import { useMemo } from 'react'

import { useConversationFiltering } from 'conversations/hooks/useConversationFiltering'
import {
  getAllConversations,
  getSubscriptionConversations,
} from 'conversations/modules/conversation/selectors'
import {
  hasUserSubscriptionAllConvosSection,
  hasUserSubscriptionAllSections,
} from 'conversations/modules/gates/selectors'
import { getAllConversationSections } from 'conversations/modules/user/sections/selectors'
import { createConditionsArray, hasQueueTimeCondition } from 'conversations/utils/conditionsUtil'
import { Pill } from 'conversations/utils/pillUtil'
import { useSelector } from 'react-redux'
import Conversation from 'shared/typings/conversations/Conversation'
import { ConversationStatus } from 'shared/typings/conversations/status/ConversationStatus'

import { RootState } from 'typesafe-actions'

const eachConvoIsUnchanged = (prev: Conversation[], cur: Conversation[]) => {
  return cur.every(({ status, id, lastOpenedAt }) => {
    const found = prev.find((c: Conversation) => c.id === id)
    if (found) {
      return found.status === status && found.lastOpenedAt === lastOpenedAt
    }
    return false
  })
}

export default function useConversationIdsBySection(
  sectionID: string,
  pill: Pill,
  inboxId?: number | null
): number[] {
  const sectionConfig = useSelector(
    (state: RootState) => getAllConversationSections(state)[sectionID]
  )
  const allConvos = useSelector(getAllConversations)
  const subscriptionConvos = useSelector(
    getSubscriptionConversations(sectionConfig.subscriptionId),
    (val, prevVal) => val.length === prevVal.length && eachConvoIsUnchanged(prevVal, val)
  )
  const hasSubChannelAllConvosSection = useSelector(hasUserSubscriptionAllConvosSection)
  const hasSubChannelAllSections = useSelector(hasUserSubscriptionAllSections)
  const useSubscriptionConvos =
    hasSubChannelAllSections ||
    (hasSubChannelAllConvosSection && sectionConfig.type === 'all-convos')

  const convosToUse = useSubscriptionConvos ? subscriptionConvos : Object.values(allConvos)
  const doFilter = !useSubscriptionConvos
  const filteredConvos: Conversation[] = useConversationFiltering(sectionID, convosToUse, doFilter)

  return useMemo(() => {
    const convoStatusMatchesSelectedPill = (
      convoStatus: ConversationStatus | undefined
    ): boolean => {
      if (pill === 'all') {
        return true
      }

      if (convoStatus === pill.toUpperCase()) {
        return true
      }

      if (convoStatus === ConversationStatus.PENDING && pill === 'open') {
        return true
      }

      return false
    }

    const convoInboxMatchesSelectedInbox = (convoInboxId: number | undefined): boolean => {
      return convoInboxId === inboxId || !inboxId
    }

    const sectionConvos: Conversation[] = filteredConvos.filter(
      (convo) =>
        convoStatusMatchesSelectedPill(convo.status) &&
        convoInboxMatchesSelectedInbox(convo.inboxId)
    )

    const sectionConditions = createConditionsArray(sectionConfig.condition)
    const isQueueTimeSection = sectionConditions ? hasQueueTimeCondition(sectionConditions) : false

    return sectionConvos
      .sort((convoA, convoB) => {
        const first = isQueueTimeSection ? convoA : convoB
        const second = isQueueTimeSection ? convoB : convoA

        return (first.lastOpenedAt ?? first.createdAt) - (second.lastOpenedAt ?? second.createdAt)
      })
      .map((c) => c.id)
  }, [filteredConvos, sectionConfig.condition, pill, inboxId])
}
